import { useMemo, useState, type ComponentProps } from "react"
import { formatNumber } from "@/v2-common/utils/utils.formatting"
import cn from "@/v2-ui/utils/utils.cn"
import PortfolioSectionHeader from "@/v2-backoffice-client/portfolio/PortfolioSectionHeader"
import UsageReportCard from "@/v2-backoffice-client/usage/report/UsageReportCard"
import PortfolioCustomerListDialog from "@/v2-backoffice-client/portfolio/PortfolioCustomerListDialog"
import UsageTrendBadge from "@/v2-backoffice-client/usage/UsageTrendBadge"
import UsageSparkline from "@/v2-backoffice-client/usage/UsageSparkline"
import { formatCompactAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import {
  APP_CODES,
  formatAppArea,
  formatAppCode,
  isMcpAppCode,
  formatTrendDetail,
  getAppCodeColor,
} from "@/v2-backoffice-client/usage/usage.constants"
import type { UsageAreaDayRow } from "@/v2-backoffice-client/usage/usage.utils"
import {
  deriveAreaIntelligence,
  type PortfolioAreaIntel,
} from "@/v2-backoffice-client/portfolio/portfolio.area.utils"
import type { PortfolioCustomerSummary } from "@/v2-backoffice-client/portfolio/portfolio.utils"

export type PortfolioAreaBreakdownProps = ComponentProps<"section"> & {
  /** Fleet daily rows — UNfiltered, the bars show the per-product split. */
  areaDays: UsageAreaDayRow[]
  /** Scoped customers — the adopter/whitespace lists derive from these. */
  customers: PortfolioCustomerSummary[]
}

type AreaDialog = {
  area: PortfolioAreaIntel
  kind: "adopters" | "whitespace"
}

// Area intelligence: every sellable capability ("bruksområde") with its
// fleet volume split by product, 28-day momentum, and — the sales meat —
// who uses it and which active accounts don't. A capability living mostly
// on Console v1 (amber) is at-risk functionality; a long whitespace list is
// the next campaign.
function PortfolioAreaBreakdown(props: PortfolioAreaBreakdownProps) {
  const { areaDays, customers, className, ...sectionProps } = props

  const [dialog, setDialog] = useState<AreaDialog | null>(null)

  const areas = useMemo(
    () => deriveAreaIntelligence(areaDays, customers),
    [areaDays, customers],
  )
  const maxVolume = Math.max(
    1,
    ...areas.map((area) => Math.max(area.current, area.previous)),
  )
  const activeCustomerCount = useMemo(
    () =>
      customers.filter((customer) => customer.summary.activeUsers28d > 0)
        .length,
    [customers],
  )
  // Feature-adoption denominator: the fleet's monthly actives.
  const activeUserCount = useMemo(
    () =>
      customers.reduce(
        (sum, customer) => sum + customer.summary.activeUsers28d,
        0,
      ),
    [customers],
  )

  // Legend: the consoles always (the split's two constants), plus any MCP
  // server that actually carries volume in the window — a third colour the
  // reader has to be able to name.
  const legendApps = useMemo(() => {
    const present = new Set(
      areas.flatMap((area) => area.appSplit.map((split) => split.app)),
    )
    return APP_CODES.filter((app) => !isMcpAppCode(app) || present.has(app))
  }, [areas])

  return (
    <section className={cn("flex flex-col gap-4", className)} {...sectionProps}>
      <PortfolioSectionHeader
        eyebrow="Use cases"
        title="What the whole base uses"
        lead="Every capability with its volume and trend, how many people use it, and the money on each side of it."
        info="Every sellable capability with its weighted 28-day volume (split by product), the previous 28 days as a grey ghost, the daily shape, user-level adoption (share of monthly-active users with it among their top areas — a floor, profiles carry each user's top 3), and adoption priced in money: the 12-month billed revenue of the customers using it, and of the active customers who don't — the value an expansion campaign plays for. 'Using' reads from the same per-user top-areas the playbook's 'Nobody has touched X' plays use, so the two always agree. Click the counts for the full lists."
      >
        <span className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
          {legendApps.map((app) => (
            <span key={app} className="flex items-center gap-1">
              <span
                className="size-2 rounded-full"
                style={{ backgroundColor: getAppCodeColor(app) }}
              />
              {formatAppCode(app)}
            </span>
          ))}
          <span>weighted usage actions · last 28 d vs previous (grey)</span>
        </span>
      </PortfolioSectionHeader>

      <UsageReportCard className="flex-1">
        {areas.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No interest-area activity in the last 28 days.
          </p>
        ) : (
          <ul className="flex flex-col gap-3">
            {areas.map((area) => (
              <li key={area.area} className="flex items-center gap-3">
                <span className="w-44 shrink-0 truncate text-xs">
                  {formatAppArea(area.area)}
                </span>

                {/* Current window split by product over a ghost of the
                  previous window, same scale — the gap IS the trend and the
                  amber share IS the migration exposure. */}
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="flex h-2 overflow-hidden rounded-full bg-muted">
                    {area.appSplit.map((split) => (
                      <span
                        key={split.app}
                        className="block h-full"
                        title={`${formatAppCode(split.app)} · ${formatNumber(Math.round(split.current))} weighted actions`}
                        style={{
                          width: `${(split.current / maxVolume) * 100}%`,
                          backgroundColor: getAppCodeColor(split.app),
                        }}
                      />
                    ))}
                  </span>
                  <span className="h-1 overflow-hidden rounded-full">
                    <span
                      className="block h-full rounded-full bg-muted-foreground/30"
                      style={{
                        width: `${(area.previous / maxVolume) * 100}%`,
                      }}
                    />
                  </span>
                </span>

                <span
                  className="w-24 shrink-0"
                  title="Daily weighted volume, last 28 days"
                >
                  <UsageSparkline series={area.daily} />
                </span>

                <span className="w-16 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                  {formatNumber(Math.round(area.current))}
                </span>

                <UsageTrendBadge
                  trendPct={area.trendPct}
                  detail={formatTrendDetail(area.current, area.previous)}
                  className="w-16 shrink-0 justify-center"
                />

                {/* Feature adoption at USER level (the customer counts to the
                  right are account-level): share of monthly actives with
                  this area among their top areas. */}
                <span
                  className="w-24 shrink-0 text-right text-xs tabular-nums text-muted-foreground"
                  title={`${formatNumber(area.userAdopters)} of ${formatNumber(activeUserCount)} monthly-active users have this among their top areas — a floor, since profiles carry each user's top 3 areas only`}
                >
                  {activeUserCount > 0
                    ? `${formatNumber(Math.round((area.userAdopters / activeUserCount) * 100))}% of users`
                    : "—"}
                </span>

                {/* Counts say who, the money says what it's worth: billed 12-mo
                  revenue of the customers behind each list. */}
                <button
                  type="button"
                  className="w-36 shrink-0 text-right text-xs tabular-nums text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                  title="Customers with tracked use of this area, and their combined 12-month billed revenue — click for the full list"
                  onClick={() => setDialog({ area, kind: "adopters" })}
                >
                  {formatNumber(area.adopters.length)}/
                  {formatNumber(activeCustomerCount)} use it
                  {area.adopterBilled12M > 0 && (
                    <>
                      {" · "}
                      {formatCompactAmount(
                        area.adopterBilled12M,
                        area.currency,
                      )}
                    </>
                  )}
                </button>

                <button
                  type="button"
                  className="w-36 shrink-0 text-right text-xs tabular-nums text-amber-500/90 underline-offset-2 hover:text-amber-400 hover:underline"
                  title="ACTIVE customers with no tracked use of this area — the expansion campaign list, worth their combined 12-month billed revenue"
                  onClick={() => setDialog({ area, kind: "whitespace" })}
                >
                  {formatNumber(area.whitespace.length)} don&apos;t
                  {area.whitespaceBilled12M > 0 ? (
                    <>
                      {" · "}
                      {formatCompactAmount(
                        area.whitespaceBilled12M,
                        area.currency,
                      )}{" "}
                      to win
                    </>
                  ) : (
                    " — pitch it"
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}

        {dialog != null && dialog.kind === "adopters" && (
          <PortfolioCustomerListDialog
            open
            onOpenChange={(next) => {
              if (!next) setDialog(null)
            }}
            title={`${formatAppArea(dialog.area.area)} — ${formatNumber(dialog.area.adopters.length)} customers using it`}
            description="Heaviest use first — the anchor accounts for references, case studies and webinar invites. Volume is their weighted activity in this area over the last 28 days; billed is their 12-month invoice total."
            detailHeader="weighted actions in this area · billed 12 mo · seats active"
            rows={dialog.area.adopters.map(({ customer, volume }) => ({
              customer,
              detail: (
                <>
                  <span className="w-20 text-right text-xs tabular-nums text-muted-foreground">
                    {formatNumber(Math.round(volume))} actions
                  </span>
                  <span className="w-16 text-right text-xs tabular-nums text-muted-foreground">
                    {customer.billed12M != null
                      ? formatCompactAmount(
                          customer.billed12M,
                          customer.primaryCurrency,
                        )
                      : "—"}
                  </span>
                  <span className="w-16 text-right text-xs tabular-nums text-muted-foreground">
                    {formatNumber(customer.summary.activeUsers28d)}/
                    {formatNumber(customer.summary.seatCount)} seats
                  </span>
                </>
              ),
            }))}
          />
        )}

        {dialog != null && dialog.kind === "whitespace" && (
          <PortfolioCustomerListDialog
            open
            onOpenChange={(next) => {
              if (!next) setDialog(null)
            }}
            title={`${formatAppArea(dialog.area.area)} — ${formatNumber(dialog.area.whitespace.length)} active customers not using it`}
            description="Accounts with tracked activity elsewhere but none in this area — the expansion campaign list. Biggest books first: money already paying is the strongest signal a demo here lands."
            detailHeader="billed 12 mo · seats active · engagement score"
            rows={dialog.area.whitespace.map((customer) => ({
              customer,
              detail: (
                <>
                  <span className="w-20 text-right text-xs tabular-nums text-muted-foreground">
                    {customer.billed12M != null
                      ? formatCompactAmount(
                          customer.billed12M,
                          customer.primaryCurrency,
                        )
                      : "—"}
                  </span>
                  <span className="w-20 text-right text-xs tabular-nums text-muted-foreground">
                    {formatNumber(customer.summary.activeUsers28d)}/
                    {formatNumber(customer.summary.seatCount)} seats
                  </span>
                  <span className="w-16 text-right text-xs tabular-nums text-muted-foreground">
                    {customer.summary.avgScore != null
                      ? `score ${customer.summary.avgScore}`
                      : "no score"}
                  </span>
                </>
              ),
            }))}
          />
        )}
      </UsageReportCard>
    </section>
  )
}

export default PortfolioAreaBreakdown
